import React, { useMemo, useState } from 'react';
import { useAsyncState } from '@/utils/hooks';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import { getResidentialUnitFilterDataByProject, getProjectDeadlines } from '@/services/projects';
import { TagInput } from '@/components/TagInput';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { InvitationStatus, TranslatableText } from '@/types';
import { ResidentialUnitFilterOptions } from '.';
import {
    Banner,
    ButtonBar,
    FilterButtons,
    FilterDropDown,
    FilterPopOut,
    SingleFilterDropDown,
    useIsFilterModified,
} from '@ygtechnologies/bygr-frontend-component-library';
import styles from './ResidentialUnitsFilter.module.scss';

type SoldStatusOption = {
    id: ResidentialUnitFilterOptions['soldStatus'];
    name: TranslatableText;
};

const soldStatusOptions: SoldStatusOption[] = [
    { id: 'all', name: <Trans>All</Trans> },
    { id: 'sold', name: <Trans>Sold</Trans> },
    { id: 'unsold', name: <Trans>Unsold</Trans> },
];

type HasUnsentOptionsOptions = {
    id: ResidentialUnitFilterOptions['hasUnsentOptions'];
    name: TranslatableText;
};

const hasUnsentOptionsOptions: HasUnsentOptionsOptions[] = [
    { id: 'all', name: <Trans>All</Trans> },
    { id: 'yes', name: <Trans>Yes</Trans> },
    { id: 'no', name: <Trans>No</Trans> },
];

type BuyerStatusOption = {
    id: ResidentialUnitFilterOptions['buyerStatus'];
    name: TranslatableText;
};

const buyerStatusOptions: BuyerStatusOption[] = [
    { id: 'all', name: <Trans>All</Trans> },
    { id: InvitationStatus.Pending, name: <Trans>Pending</Trans> },
    { id: InvitationStatus.Sent, name: <Trans>Sent</Trans> },
    { id: InvitationStatus.Registered, name: <Trans>Registered</Trans> },
    { id: InvitationStatus.Failed, name: <Trans>Failed</Trans> },
    { id: InvitationStatus.Canceled, name: <Trans>Canceled</Trans> },
];

interface ProjectResidentialUnitFilterProps {
    filter: ResidentialUnitFilterOptions;
    onFilterChange: (f: ResidentialUnitFilterOptions) => void;
    isLoading: boolean;
}

const ProjectResidentialUnitFilter: React.FC<ProjectResidentialUnitFilterProps> = ({
    filter,
    onFilterChange,
    isLoading,
}) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const [isOpen, setIsOpen] = useState(false);
    const [filterData] = useAsyncState(() => getResidentialUnitFilterDataByProject(projectId), [], null); //TODO: fix this so it doesn't reload all the time
    const [deadlines] = useAsyncState(() => getProjectDeadlines(projectId), [], null);
    const filterModified = useIsFilterModified(filter, ResidentialUnitFilterOptions.empty);

    return (
        <div className={styles.filterWrapper}>
            <ButtonBar>
                <FilterButtons
                    searchText={filter.searchText}
                    onSearchTextChange={(v) => onFilterChange({ ...filter, searchText: v })}
                    isLoading={isLoading}
                    onButtonClick={() => setIsOpen((v) => !v)}
                    isActive={filterModified}
                />
            </ButtonBar>
            <FilterPopOut
                onClose={() => setIsOpen(false)}
                onClear={() => onFilterChange(ResidentialUnitFilterOptions.empty())}
                isVisible={isOpen}>
                {filterData && (
                    <>
                        <FilterDropDown
                            label={t`Floor`}
                            placeholder={t`Any floor`}
                            data={filterData.floors.map((f) => ({ id: f, name: f }))!}
                            value={filter.floors}
                            onChange={(v) => onFilterChange({ ...filter, floors: v.map((x) => x.id) })}
                        />

                        <FilterDropDown
                            label={t`Layout types`}
                            placeholder={t`Any layout type`}
                            data={filterData.layoutTypes.map((f) => ({ id: f, name: f }))!}
                            value={filter.layoutTypes}
                            onChange={(v) => onFilterChange({ ...filter, layoutTypes: v.map((x) => x.id) })}
                        />

                        <FilterDropDown
                            label={t`Unit groups`}
                            placeholder={t`Any unit groups`}
                            data={filterData.groups.map((f) => ({ id: f.id, name: f.name }))}
                            value={filter.groups}
                            onChange={(v) => onFilterChange({ ...filter, groups: v.map((x) => x.id) })}
                        />
                        <label className="filter-label">
                            <Trans>Tags</Trans>
                        </label>
                        <TagInput
                            value={filter.tags}
                            placeholder={t`Tags`}
                            onTagsChange={(tags) => onFilterChange({ ...filter, tags: tags.map((t) => t.id) })}
                            projectId={projectId}
                        />
                        <FilterDropDown
                            label={t`Rooms`}
                            placeholder={t`Any room`}
                            data={filterData.rooms?.map((r) => ({ id: r.id, name: r.label })) ?? []}
                            value={filter.rooms}
                            onChange={(v) => onFilterChange({ ...filter, rooms: v.map((x) => x.id) })}
                        />
                        <SingleFilterDropDown
                            label={t`Sold/unsold`}
                            placeholder={t`Sold/unsold`}
                            data={soldStatusOptions}
                            value={filter.soldStatus}
                            onChange={(nv) => onFilterChange({ ...filter, soldStatus: nv!.id })}
                        />
                        <FilterDropDown
                            label={t`Deadlines`}
                            placeholder={t`Any deadline`}
                            data={deadlines?.map((x) => ({ id: x.id, name: x.name })) ?? []}
                            value={filter.hasDeadline}
                            onChange={(v) => onFilterChange({ ...filter, hasDeadline: v.map((x) => x.id) })}
                        />
                        <SingleFilterDropDown
                            label={t`Has unsent options`}
                            placeholder={t`Has unsent options`}
                            data={hasUnsentOptionsOptions}
                            value={filter.hasUnsentOptions}
                            onChange={(nv) => onFilterChange({ ...filter, hasUnsentOptions: nv!.id })}
                        />
                        <SingleFilterDropDown
                            label={t`With buyer status`}
                            placeholder={t`With buyer status`}
                            data={buyerStatusOptions}
                            value={filter.buyerStatus}
                            onChange={(nv) => onFilterChange({ ...filter, buyerStatus: nv!.id })}
                        />

                        {filter.hasUnsentOptions && filter.hasDeadline && (
                            <Banner type="info">
                                <Trans>
                                    <strong>Please note</strong> when selecting both deadline and unsent options filter, it
                                    will only show units that has unsent/sent options in the selected deadlines.
                                </Trans>
                            </Banner>
                        )}
                    </>
                )}
            </FilterPopOut>
        </div>
    );
};

export default ProjectResidentialUnitFilter;
